import { makeObservable, observable, action } from "mobx"
import { Survey } from "../../../domain/entities/Survey"
import { SurveySection } from "../../../domain/entities/SurveySection"
import { SectionQuestion } from "../../../domain/entities/SectionQuestion"
import { Microsite } from "../../../domain/entities/Microsite"
import { GetSurveysUseCase } from "../../../domain/useCases/surveys/getSurveys"
import { GetSurveySectionsUseCase } from "../../../domain/useCases/surveys/getSurveySections"
import { CreateSurveyUseCase } from "../../../domain/useCases/surveys/createSurvey"
import { UpdateSurveyUseCase } from "../../../domain/useCases/surveys/updateSurvey"
import { DeleteSurveyUseCase } from "../../../domain/useCases/surveys/deleteSurvey"
import { CreateSurveySectionUseCase } from "../../../domain/useCases/surveys/createSurveySection"
import { UpdateSurveySectionUseCase } from "../../../domain/useCases/surveys/updateSurveySection"
import { DeleteSurveySectionUseCase } from "../../../domain/useCases/surveys/deleteSurveySection"
import { UpdateSurveyCompletionUseCase } from "../../../domain/useCases/surveys/updateSurveyCompletion"
import { UpdateSurveyCompletionRequest } from "../../../domain/useCases/surveys/updateSurveyCompletion/request"
import { ExportSurveysUseCase } from "../../../domain/useCases/surveys/exportSurveys"
import { GetMicrositesUseCase } from "../../../domain/useCases/microsites/getMicrosites"

import { TableConfig } from "../../components/DataTable/types/TableConfig"
import { ViewModel } from "../ViewModel"
import { UploadFile } from "antd"
import { ErrorHandler } from "../../error/ErrorHandler"
import { FileService } from "../../../domain/services/FileService"
import { ExportSurveyOptionsUseCase } from "../../../domain/useCases/surveys/exportSurveyOptions"
import { SurveyService } from "../../../data/services/SurveyService"

export interface SurveySectionFormData {
	id?: number
	name: string
	name_en: string
	name_ca: string
	description?: string
	description_en?: string
	description_ca?: string
	image?: UploadFile | string
	order?: number
	questions?: SectionQuestion[]
}

export interface SurveyFormData {
	id?: number
	title: string
	title_en: string
	title_ca: string
	description?: string
	description_en?: string
	description_ca?: string
	microsites?: Microsite[]
	marketplaces?: ("ES" | "MX")[]
	sections?: SurveySection[]
	status: boolean
	image?: UploadFile | string
	adminRecommendation?: string
}

interface Props {
	GetSurveysUseCase: GetSurveysUseCase
	GetSurveySectionsUseCase: GetSurveySectionsUseCase
	CreateSurveyUseCase: CreateSurveyUseCase
	UpdateSurveyUseCase: UpdateSurveyUseCase
	DeleteSurveyUseCase: DeleteSurveyUseCase
	CreateSurveySectionUseCase: CreateSurveySectionUseCase
	UpdateSurveySectionUseCase: UpdateSurveySectionUseCase
	DeleteSurveySectionUseCase: DeleteSurveySectionUseCase
	UpdateSurveyCompletionUseCase: UpdateSurveyCompletionUseCase
	ExportSurveysUseCase: ExportSurveysUseCase
	ExportSurveyOptionsUseCase: ExportSurveyOptionsUseCase
	GetMicrositesUseCase: GetMicrositesUseCase
	SurveyService: SurveyService
	ErrorHandler: ErrorHandler
}
export class SurveysViewModel extends ViewModel {
	private _getSurveysUseCase
	private _getSurveySectionsUseCase
	private _createSurveyUseCase
	private _updateSurveyUseCase
	private _deleteSurveyUseCase
	private _createSurveySectionUseCase
	private _updateSurveySectionUseCase
	private _deleteSurveySectionUseCase
	private _updateSurveyCompletionUseCase
	private _exportSurveysUseCase
	private _exportSurveyOptionsUseCase
	private _getMicrositesUseCase
	private _surveyService: SurveyService
	private _errorHandler

	surveys: Survey[] = []
	surveySections: SurveySection[] = []
	microsites: Microsite[] = []
	isLoading: boolean = false
	error: string = ""
	searchValue: string = ""
	searchedSurveys: Survey[] = []
	searchedSurveySections: SurveySection[] = []
	sectionFormData: SurveySectionFormData = {
		name: "",
		name_en: "",
		name_ca: "",
		description: "",
		description_en: "",
		description_ca: "",
		image: undefined,
		order: 0
	}
	surveyFormData: SurveyFormData = {
		title: "",
		title_en: "",
		title_ca: "",
		description: "",
		description_en: "",
		description_ca: "",
		microsites: [],
		marketplaces: [],
		sections: [],
		status: true,
		image: undefined
	}
	isEditMode: boolean = false
	uploadingImage: boolean = false
	tableConfig: TableConfig = {
		pageSize: 20,
		sort: { order: "descend", field: "id" }
	}

	constructor({
		GetSurveysUseCase,
		GetSurveySectionsUseCase,
		CreateSurveyUseCase,
		DeleteSurveyUseCase,
		UpdateSurveyUseCase,
		CreateSurveySectionUseCase,
		UpdateSurveySectionUseCase,
		DeleteSurveySectionUseCase,
		UpdateSurveyCompletionUseCase,
		ExportSurveysUseCase,
		ExportSurveyOptionsUseCase,
		GetMicrositesUseCase,
		SurveyService,
		ErrorHandler
	}: Props) {
		super()
		makeObservable(this, {
			searchValue: observable,
			searchedSurveys: observable,
			searchedSurveySections: observable,
			surveys: observable,
			surveySections: observable,
			microsites: observable,
			sectionFormData: observable,
			surveyFormData: observable,
			isEditMode: observable,
			uploadingImage: observable,
			isLoading: observable,
			error: observable,
			tableConfig: observable,
			setSurveys: action,
			setSurveySections: action,
			setSearchedSurveys: action,
			setSearchedSurveySections: action,
			setMicrosites: action,
			setSectionFormData: action,
			setSurveyFormData: action,
			setEditMode: action,
			setUploadingImage: action,
			setTableConfig: action,
			setLoading: action,
			setSearchValue: action,
			setError: action
		})
		this._getSurveysUseCase = GetSurveysUseCase
		this._getSurveySectionsUseCase = GetSurveySectionsUseCase
		this._createSurveyUseCase = CreateSurveyUseCase
		this._updateSurveyUseCase = UpdateSurveyUseCase
		this._deleteSurveyUseCase = DeleteSurveyUseCase
		this._createSurveySectionUseCase = CreateSurveySectionUseCase
		this._updateSurveySectionUseCase = UpdateSurveySectionUseCase
		this._deleteSurveySectionUseCase = DeleteSurveySectionUseCase
		this._updateSurveyCompletionUseCase = UpdateSurveyCompletionUseCase
		this._exportSurveysUseCase = ExportSurveysUseCase
		this._exportSurveyOptionsUseCase = ExportSurveyOptionsUseCase
		this._getMicrositesUseCase = GetMicrositesUseCase
		this._surveyService = SurveyService
		this._errorHandler = ErrorHandler
		this.fetchSurveys()
		this.fetchSurveySections()
		this.fetchMicrosites()
	}

	async fetchSurveys() {
		this.setLoading(true)
		try {
			const surveys = await this._getSurveysUseCase.exec()
			this.setSurveys(surveys)
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	async createSurvey() {
		this.setLoading(true)
		try {
			const createdSurvey = await this._createSurveyUseCase.exec(new Survey(this.surveyFormData))
			const mergedData = {
				...this.surveyFormData,
				...createdSurvey,
				id: createdSurvey.id,
				title_en: createdSurvey.title_en || this.surveyFormData.title_en,
				title_ca: createdSurvey.title_ca || this.surveyFormData.title_ca,
				description_en: createdSurvey.description_en || this.surveyFormData.description_en,
				description_ca: createdSurvey.description_ca || this.surveyFormData.description_ca,
				microsites:
					createdSurvey.microsites && createdSurvey.microsites.length > 0
						? createdSurvey.microsites
						: this.surveyFormData.microsites,
				marketplaces:
					createdSurvey.marketplaces && createdSurvey.marketplaces.length > 0
						? createdSurvey.marketplaces
						: this.surveyFormData.marketplaces,
				sections:
					createdSurvey.sections && createdSurvey.sections.length > 0
						? createdSurvey.sections
						: this.surveyFormData.sections,
				image: createdSurvey.image || this.surveyFormData.image
			}

			this.setSurveyFormData(mergedData)
			this.setEditMode(true)
			await this.fetchSurveys()
			return createdSurvey.id
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	async updateSurvey() {
		this.setLoading(true)
		try {
			const updatedSurvey = await this._updateSurveyUseCase.exec(new Survey(this.surveyFormData))
			const mergedData = {
				...this.surveyFormData,
				...updatedSurvey,
				title_en: updatedSurvey.title_en || this.surveyFormData.title_en,
				title_ca: updatedSurvey.title_ca || this.surveyFormData.title_ca,
				description_en: updatedSurvey.description_en || this.surveyFormData.description_en,
				description_ca: updatedSurvey.description_ca || this.surveyFormData.description_ca,
				microsites:
					updatedSurvey.microsites && updatedSurvey.microsites.length > 0
						? updatedSurvey.microsites
						: this.surveyFormData.microsites,
				marketplaces:
					updatedSurvey.marketplaces && updatedSurvey.marketplaces.length > 0
						? updatedSurvey.marketplaces
						: this.surveyFormData.marketplaces,
				sections:
					updatedSurvey.sections && updatedSurvey.sections.length > 0
						? updatedSurvey.sections
						: this.surveyFormData.sections,
				image: updatedSurvey.image || this.surveyFormData.image
			}

			this.setSurveyFormData(mergedData)
			await this.fetchSurveys()
		} catch (error: any) {
			console.log(error)
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	async deleteSurvey(id: string) {
		this.setLoading(true)
		try {
			await this._deleteSurveyUseCase.exec(id)
			await this.fetchSurveys()
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	async createSurveySection() {
		this.setLoading(true)
		try {
			const section = await this._createSurveySectionUseCase.exec(new SurveySection(this.sectionFormData))
			this.setSectionFormData({
				...this.sectionFormData,
				image: section.image,
				id: section.id
			})
			this.setEditMode(true)
			await this.fetchSurveySections()
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	async updateSurveySection() {
		this.setLoading(true)
		try {
			const section = await this._updateSurveySectionUseCase.exec(new SurveySection(this.sectionFormData))
			this.setSectionFormData({
				...this.sectionFormData,
				image: section.image
			})
			await Promise.all([this.fetchSurveySections(), this.fetchSurveys()])
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	async deleteSurveySection(id: number) {
		this.setLoading(true)
		try {
			await this._deleteSurveySectionUseCase.exec(id)
			await this.fetchSurveySections()
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	async fetchSurveySections() {
		this.setLoading(true)
		try {
			const surveySections = await this._getSurveySectionsUseCase.exec()
			this.setSurveySections(surveySections)
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	async fetchMicrosites() {
		this.setLoading(true)
		try {
			const microsites = await this._getMicrositesUseCase.exec()
			this.setMicrosites(microsites)
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	async exportSurveys(surveyId: number, surveyTitle: string) {
		try {
			this.setLoading(true)
			const file = await this._exportSurveysUseCase.exec({ surveyId })
			const fileService = new FileService()
			const dateString = new Date().toISOString().split("T")[0]
			fileService.dowloadFile(file, `Survey_${surveyTitle}_Export_${dateString}.xlsx`)
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	async exportSurveyOptions(surveyId: number, surveyTitle: string) {
		try {
			this.setLoading(true)
			const file = await this._exportSurveyOptionsUseCase.exec({ surveyId })
			const fileService = new FileService()
			const dateString = new Date().toISOString().split("T")[0]
			fileService.dowloadFile(file, `Survey_${surveyTitle}_Options_Report_${dateString}.xlsx`)
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	async exportSurveyCommentaryPDF(surveyId: number, surveyTitle: string) {
		try {
			this.setLoading(true)
			await this._surveyService.exportSurveyCommentaryPDF(surveyId, surveyTitle)
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	async exportSurveyUserPDF(surveyId: number, userId: number, surveyTitle: string, userEmail: string) {
		try {
			this.setLoading(true)
			await this._surveyService.exportSurveyUserPDF(surveyId, userId, surveyTitle, userEmail)
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	async sendSurveyEmail(
		surveyId: number,
		userId: number,
		surveyTitle: string,
		userEmail: string
	): Promise<{ success: boolean; message: string }> {
		try {
			this.setLoading(true)
			const result = await this._surveyService.sendSurveyEmail(surveyId, userId, surveyTitle, userEmail)
			return result
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	setSearchValue(value: string) {
		this.searchValue = value
	}

	setSectionFormData(formData: Partial<SurveySectionFormData>) {
		this.sectionFormData = { ...this.sectionFormData, ...formData }
	}

	setSurveyFormData(formData: Partial<SurveyFormData>) {
		this.surveyFormData = { ...this.surveyFormData, ...formData }
	}

	setEditMode(isEditMode: boolean) {
		this.isEditMode = isEditMode
	}

	setUploadingImage(uploadingImage: boolean) {
		this.uploadingImage = uploadingImage
	}

	setSurveys(surveys: Survey[]) {
		this.surveys = surveys
		this.setSearchedSurveys(surveys)
	}

	setSurveySections(surveySections: SurveySection[]) {
		this.surveySections = surveySections
		this.setSearchedSurveySections(surveySections)
	}

	setMicrosites(microsites: Microsite[]) {
		this.microsites = microsites
	}

	setLoading(loading: boolean) {
		this.isLoading = loading
	}

	setError(error: string) {
		this.error = error
	}

	setSearchedSurveys(surveys: Survey[]) {
		this.searchedSurveys = surveys
	}

	setSearchedSurveySections(surveySections: SurveySection[]) {
		this.searchedSurveySections = surveySections
	}

	setTableConfig(config: Partial<typeof this.tableConfig>) {
		this.tableConfig = { ...this.tableConfig, ...config }
	}

	async updateSurveyCompletion(request: UpdateSurveyCompletionRequest) {
		this.setLoading(true)
		try {
			const result = await this._updateSurveyCompletionUseCase.exec(request)
			await this.fetchSurveys()
			return result
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}
}
